/**
 * Tool Readiness Store
 * 
 * Single source of truth for tool availability evaluation (Batch 3).
 * 
 * ============================================================================
 * INVARIANTS (Doc 06, Doc 08)
 * ============================================================================
 * 
 * 1. This store is the ONLY client source for tool availability.
 * 2. UI MUST NOT compute or infer tool availability outside this store.
 * 3. Tool availability is derived from server evaluation only.
 * 4. Blocked tools show diagnostic blockers, not disabled buttons.
 * 
 * Tool prerequisites map to existing readiness artifacts (Doc 03/04).
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, isSupabaseAvailable } from '../../lib/supabaseSync';

// ============================================================================
// Types
// ============================================================================

/**
 * Blocker for a tool that is not available.
 */
export interface ToolBlocker {
  code: string;
  title: string;
  why_it_matters: string;
  current_state: string;
  remediation_route: string;
}

/**
 * Result of evaluating a single tool's readiness.
 */
export interface ToolReadinessResult {
  tool_name: string;
  cycle_id: string;
  available: boolean;
  evaluated_at: string;
  blockers: ToolBlocker[];
  error?: string;
}

/**
 * Result of evaluating all tools for a cycle.
 */
export interface AllToolsReadinessResult {
  cycle_id: string;
  evaluated_at: string;
  tools: Record<string, {
    available: boolean;
    blockers: ToolBlocker[];
  }>;
  error?: string;
}

// ============================================================================
// Store Interface
// ============================================================================

interface ToolReadinessStore {
  // State
  /** Map of cycleId -> tool readiness results */
  allToolsReadiness: Record<string, AllToolsReadinessResult>;
  /** Map of cycleId -> loading state */
  isLoading: Record<string, boolean>;
  /** Map of cycleId -> error message */
  error: Record<string, string | null>;
  /** Map of cycleId -> last evaluation timestamp */
  lastEvaluated: Record<string, Date>;
  
  // Actions
  /**
   * Evaluate readiness for a single tool.
   */
  evaluateToolReadiness: (cycleId: string, toolName: string) => Promise<ToolReadinessResult | null>;
  
  /**
   * Evaluate readiness for all tools in a cycle.
   * This is the primary method for populating tool selection UI.
   */
  evaluateAllTools: (cycleId: string) => Promise<AllToolsReadinessResult | null>;
  
  /**
   * Clear cached tool readiness for a cycle (forces re-evaluation).
   */
  clearToolReadiness: (cycleId: string) => void;
  
  // Selectors
  /**
   * Check if a specific tool is available for a cycle.
   */
  isToolAvailable: (cycleId: string, toolName: string) => boolean;
  
  /**
   * Get blockers for a specific tool.
   */
  getToolBlockers: (cycleId: string, toolName: string) => ToolBlocker[];
  
  /**
   * Get all available tools for a cycle.
   */
  getAvailableTools: (cycleId: string) => string[];
  
  /**
   * Get all blocked tools for a cycle with their blockers.
   */
  getBlockedTools: (cycleId: string) => Array<{ toolName: string; blockers: ToolBlocker[] }>;
  
  /**
   * Check if tool readiness is currently loading.
   */
  isEvaluating: (cycleId: string) => boolean;
  
  /**
   * Get the error for a cycle evaluation, if any.
   */
  getError: (cycleId: string) => string | null;
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useToolReadinessStore = create<ToolReadinessStore>((set, get) => ({
  // Initial state
  allToolsReadiness: {},
  isLoading: {},
  error: {},
  lastEvaluated: {},
  
  // ========================================================================
  // Actions
  // ========================================================================
  
  evaluateToolReadiness: async (cycleId: string, toolName: string) => {
    try {
      // Check Supabase availability
      if (!isSupabaseAvailable()) {
        console.warn('[ToolReadinessStore] Supabase not available');
        return null;
      }
      
      const supabase = getSafeSupabaseClient();
      if (!supabase) {
        return null;
      }
      
      // Call the tool readiness evaluator RPC
      const { data, error } = await supabase.rpc('evaluate_tool_readiness', {
        p_cycle_id: cycleId,
        p_tool_name: toolName,
      });
      
      if (error) {
        console.error('[ToolReadinessStore] RPC error:', error);
        return null;
      }
      
      const result = data as ToolReadinessResult;
      
      console.log('[ToolReadinessStore] Evaluated tool readiness:', toolName, {
        available: result.available,
        blockerCount: result.blockers?.length ?? 0,
      });
      
      return result;
      
    } catch (err) {
      console.error('[ToolReadinessStore] Exception:', err);
      return null;
    }
  },
  
  evaluateAllTools: async (cycleId: string) => {
    // Mark as loading
    set(state => ({
      isLoading: { ...state.isLoading, [cycleId]: true },
      error: { ...state.error, [cycleId]: null },
    }));
    
    try {
      // Check Supabase availability
      if (!isSupabaseAvailable()) {
        console.warn('[ToolReadinessStore] Supabase not available, using fallback');
        set(state => ({
          isLoading: { ...state.isLoading, [cycleId]: false },
          error: { ...state.error, [cycleId]: 'Supabase not available' },
        }));
        return null;
      }
      
      const supabase = getSafeSupabaseClient();
      if (!supabase) {
        set(state => ({
          isLoading: { ...state.isLoading, [cycleId]: false },
          error: { ...state.error, [cycleId]: 'Supabase client not initialized' },
        }));
        return null;
      }
      
      // Call the batch tool readiness evaluator RPC
      const { data, error } = await supabase.rpc('evaluate_all_tools_readiness', {
        p_cycle_id: cycleId,
      });
      
      if (error) {
        console.error('[ToolReadinessStore] RPC error:', error);
        set(state => ({
          isLoading: { ...state.isLoading, [cycleId]: false },
          error: { ...state.error, [cycleId]: error.message },
        }));
        return null;
      }
      
      // Check for error response from the function
      if (data && 'error' in data) {
        console.error('[ToolReadinessStore] Evaluator error:', data.error);
        set(state => ({
          isLoading: { ...state.isLoading, [cycleId]: false },
          error: { ...state.error, [cycleId]: data.error as string },
        }));
        return null;
      }
      
      // Store successful result
      const result = data as AllToolsReadinessResult;
      set(state => ({
        allToolsReadiness: { ...state.allToolsReadiness, [cycleId]: result },
        isLoading: { ...state.isLoading, [cycleId]: false },
        error: { ...state.error, [cycleId]: null },
        lastEvaluated: { ...state.lastEvaluated, [cycleId]: new Date() },
      }));
      
      // Log summary
      const tools = result.tools ?? {};
      const availableCount = Object.values(tools).filter(t => t.available).length;
      const blockedCount = Object.values(tools).filter(t => !t.available).length;
      
      console.log('[ToolReadinessStore] Evaluated all tools for cycle:', cycleId, {
        total: Object.keys(tools).length,
        available: availableCount,
        blocked: blockedCount,
      });
      
      return result;
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('[ToolReadinessStore] Exception:', err);
      set(state => ({
        isLoading: { ...state.isLoading, [cycleId]: false },
        error: { ...state.error, [cycleId]: errorMessage },
      }));
      return null;
    }
  },
  
  clearToolReadiness: (cycleId: string) => {
    set(state => {
      const { [cycleId]: _, ...restReadiness } = state.allToolsReadiness;
      const { [cycleId]: __, ...restLoading } = state.isLoading;
      const { [cycleId]: ___, ...restError } = state.error;
      const { [cycleId]: ____, ...restLastEvaluated } = state.lastEvaluated;
      return {
        allToolsReadiness: restReadiness,
        isLoading: restLoading,
        error: restError,
        lastEvaluated: restLastEvaluated,
      };
    });
  },
  
  // ========================================================================
  // Selectors
  // ========================================================================
  
  isToolAvailable: (cycleId: string, toolName: string) => {
    const result = get().allToolsReadiness[cycleId];
    if (!result?.tools) return false;
    return result.tools[toolName]?.available ?? false;
  },
  
  getToolBlockers: (cycleId: string, toolName: string) => {
    const result = get().allToolsReadiness[cycleId];
    if (!result?.tools) return [];
    return result.tools[toolName]?.blockers ?? [];
  },
  
  getAvailableTools: (cycleId: string) => {
    const result = get().allToolsReadiness[cycleId];
    if (!result?.tools) return [];
    return Object.entries(result.tools)
      .filter(([_, tool]) => tool.available)
      .map(([name]) => name);
  },
  
  getBlockedTools: (cycleId: string) => {
    const result = get().allToolsReadiness[cycleId];
    if (!result?.tools) return [];
    return Object.entries(result.tools)
      .filter(([_, tool]) => !tool.available)
      .map(([name, tool]) => ({ toolName: name, blockers: tool.blockers }));
  },
  
  isEvaluating: (cycleId: string) => {
    return get().isLoading[cycleId] ?? false;
  },
  
  getError: (cycleId: string) => {
    return get().error[cycleId] ?? null;
  },
}));

// ============================================================================
// Hook Helpers
// ============================================================================

/**
 * Hook to get all tool availability for a cycle.
 * Returns { availableTools, blockedTools, isLoading, error }.
 */
export function useToolsReadiness(cycleId: string) {
  const result = useToolReadinessStore(state => state.allToolsReadiness[cycleId]);
  const isLoading = useToolReadinessStore(state => state.isLoading[cycleId]);
  const error = useToolReadinessStore(state => state.error[cycleId]);
  
  if (!result?.tools) {
    return {
      availableTools: [] as string[],
      blockedTools: [] as Array<{ toolName: string; blockers: ToolBlocker[] }>,
      isLoading: isLoading ?? false,
      error: error ?? null,
    };
  }
  
  const availableTools = Object.entries(result.tools)
    .filter(([_, tool]) => tool.available)
    .map(([name]) => name);
    
  const blockedTools = Object.entries(result.tools)
    .filter(([_, tool]) => !tool.available)
    .map(([name, tool]) => ({ toolName: name, blockers: tool.blockers }));
  
  return {
    availableTools,
    blockedTools,
    isLoading: isLoading ?? false,
    error: error ?? null,
  };
}

/**
 * Hook to get readiness for a specific tool.
 * Returns { available, blockers, isLoading }.
 */
export function useToolReadiness(cycleId: string, toolName: string) {
  const result = useToolReadinessStore(state => state.allToolsReadiness[cycleId]);
  const isLoading = useToolReadinessStore(state => state.isLoading[cycleId]);
  
  if (!result?.tools || !result.tools[toolName]) {
    return {
      available: false,
      blockers: [] as ToolBlocker[],
      isLoading: isLoading ?? false,
    };
  }
  
  const tool = result.tools[toolName];
  
  return {
    available: tool.available,
    blockers: tool.blockers,
    isLoading: isLoading ?? false,
  };
}
